import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { getPlanStatusKind, getTrialDaysLeft, formatPeriodEnd } from "../utils/planStatus";

/**
 * Shared header for the authenticated app (Dashboard + Analytics + Team) —
 * logo, health dot, nav group, user, logout.
 *
 * Below `md:`, nav/user/logout collapse behind a hamburger — adapted from
 * marketing/MarketingNav.js's proven pattern (same Heroicons, same
 * hidden md:flex / md:hidden split), not a `flex-wrap` cram. One real
 * difference from MarketingNav: this header isn't sticky/fixed-height, so
 * the panel renders inline in normal document flow (pushes content down)
 * rather than as a `fixed` full-screen overlay — no body-scroll-lock needed
 * either, since nothing is being overlaid.
 */
const WS_STATUS_BADGES = {
  connected: { label: "● Live", color: "#00d4aa" },
  connecting: { label: "● Connecting…", color: "#8b95ab" },
  reconnecting: { label: "● Reconnecting…", color: "#8b95ab" },
  offline: { label: "● Offline", color: "#ffa502" },
};

// Item 9: mirrors WS_STATUS_BADGES's shape/styling exactly, so the two
// badges read as one consistent "status pill" language in the header.
function planBadge(plan) {
  const kind = getPlanStatusKind(plan);
  if (kind === "trial") {
    return { label: `Trial · ${getTrialDaysLeft(plan)}d left`, color: "#0099ff" };
  }
  if (kind === "dunning_grace") return { label: "Payment issue", color: "#ffa502" };
  if (kind === "free") return { label: "Free plan", color: "#8b95ab" };
  // Item 24: still Pro (and still teal — access is genuinely unchanged),
  // but says when it runs out. Falls back to a plain "Pro" if the date is
  // unknown rather than rendering "Pro · ends ".
  if (kind === "pro_cancelling") {
    const ends = formatPeriodEnd(plan);
    return { label: ends ? `Pro · ends ${ends}` : "Pro", color: "#00d4aa" };
  }
  if (kind === "pro") return { label: "Pro", color: "#00d4aa" };
  return null;
}

function AppHeader({ user, onLogout, wsStatus, plan, active }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const wsBadge = WS_STATUS_BADGES[wsStatus];
  const planPill = planBadge(plan);
  const profileIncomplete = !user?.contact_email;

  // Item 22: close on Escape or a click outside the menu — same conventions
  // ConfirmDialog.js already uses for its own keydown/backdrop handling.
  useEffect(() => {
    if (!menuOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen]);

  const navLinkClass = (isActive) =>
    `text-sm font-medium transition-colors ${
      isActive ? "text-rf-text-primary" : "text-rf-text-secondary hover:text-rf-text-primary"
    }`;

  const mobileNavLinkClass = (isActive) =>
    `text-base font-medium transition-colors ${
      isActive ? "text-rf-text-primary" : "text-rf-text-secondary hover:text-rf-text-primary"
    }`;

  const closeMobile = () => setMobileOpen(false);

  const displayName = user?.display_name || user?.github_username || "User";
  const initial = displayName.charAt(0).toUpperCase();

  return (
    <header className="border-b border-rf-border">
      <div className="px-4 sm:px-8 py-4 flex justify-between items-center gap-4">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-rf-accent to-rf-accent-blue flex items-center justify-center text-white font-bold shrink-0">
            A
          </div>
          <span className="text-lg font-bold text-rf-text-primary tracking-tight">
            Anthurium
          </span>
          <span className="hidden sm:inline-block text-[11px] text-rf-text-muted bg-rf-bg-tertiary px-2 py-0.5 rounded ml-1">
            v1.0
          </span>
          {wsBadge && (
            <span
              className="hidden md:inline-block text-[10px] px-2 py-0.5 rounded-full ml-2"
              style={{ color: wsBadge.color, backgroundColor: `${wsBadge.color}18` }}
            >
              {wsBadge.label}
            </span>
          )}
          {planPill && (
            <span
              className="hidden md:inline-block text-[10px] px-2 py-0.5 rounded-full ml-2"
              style={{ color: planPill.color, backgroundColor: `${planPill.color}18` }}
            >
              {planPill.label}
            </span>
          )}
          <nav className="hidden md:flex items-center gap-4 ml-6">
            <Link to="/dashboard" className={navLinkClass(active === "dashboard")}>
              Dashboard
            </Link>
            <Link to="/analytics" className={navLinkClass(active === "analytics")}>
              Analytics
            </Link>
            <Link to="/team" className={navLinkClass(active === "team")}>
              Team
            </Link>
          </nav>
        </div>

        <div className="hidden md:block relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            className="flex items-center gap-3"
          >
            <span className="text-sm text-rf-text-secondary">{displayName}</span>
            <div className="relative w-8 h-8 rounded-full bg-rf-accent-blue/15 flex items-center justify-center text-xs text-rf-accent-blue font-semibold shrink-0">
              {initial}
              {profileIncomplete && (
                <span
                  className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-rf-accent-blue"
                  aria-hidden="true"
                />
              )}
            </div>
          </button>

          {menuOpen && (
            <div
              role="menu"
              className="absolute right-0 mt-2 w-48 bg-rf-bg-secondary border border-rf-border rounded-lg shadow-lg z-50 py-1"
            >
              <Link
                to="/profile"
                role="menuitem"
                onClick={() => setMenuOpen(false)}
                className="flex items-center justify-between px-4 py-2 text-sm text-rf-text-secondary hover:text-rf-text-primary hover:bg-rf-bg-tertiary transition-colors"
              >
                My Profile
                {profileIncomplete && (
                  <span className="w-1.5 h-1.5 rounded-full bg-rf-accent-blue" aria-hidden="true" />
                )}
              </Link>
              <Link
                to="/team"
                role="menuitem"
                onClick={() => setMenuOpen(false)}
                className="block px-4 py-2 text-sm text-rf-text-secondary hover:text-rf-text-primary hover:bg-rf-bg-tertiary transition-colors"
              >
                Team
              </Link>
              {onLogout && (
                <>
                  <div className="border-t border-rf-border my-1" role="separator" />
                  <button
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      onLogout();
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-rf-text-secondary hover:text-rf-text-primary hover:bg-rf-bg-tertiary transition-colors"
                  >
                    Logout
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        <button
          type="button"
          className="flex h-11 w-11 items-center justify-center rounded-lg text-rf-text-primary md:hidden shrink-0"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((v) => !v)}
        >
          {mobileOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-rf-border px-4 sm:px-8 py-4 flex flex-col gap-4">
          <nav className="flex flex-col gap-3" aria-label="Mobile">
            <Link to="/dashboard" onClick={closeMobile} className={mobileNavLinkClass(active === "dashboard")}>
              Dashboard
            </Link>
            <Link to="/analytics" onClick={closeMobile} className={mobileNavLinkClass(active === "analytics")}>
              Analytics
            </Link>
            <Link to="/team" onClick={closeMobile} className={mobileNavLinkClass(active === "team")}>
              Team
            </Link>
            <Link to="/profile" onClick={closeMobile} className={mobileNavLinkClass(active === "profile")}>
              My Profile
              {profileIncomplete && (
                <span
                  className="inline-block w-1.5 h-1.5 rounded-full bg-rf-accent-blue ml-2 align-middle"
                  aria-hidden="true"
                />
              )}
            </Link>
          </nav>
          <div className="border-t border-rf-border pt-4 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="relative w-8 h-8 rounded-full bg-rf-accent-blue/15 flex items-center justify-center text-xs text-rf-accent-blue font-semibold shrink-0">
                {initial}
                {profileIncomplete && (
                  <span
                    className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-rf-accent-blue"
                    aria-hidden="true"
                  />
                )}
              </div>
              <span className="text-sm text-rf-text-secondary">{displayName}</span>
              {/* Item 20: previously hidden md:, so mobile users had zero
                  indication of trial/plan status, ever. Same "collapse behind
                  the hamburger, expand on tap" treatment already used for
                  nav/user/logout above — not a special case. */}
              {planPill && (
                <span
                  className="text-[10px] px-2 py-0.5 rounded-full"
                  style={{ color: planPill.color, backgroundColor: `${planPill.color}18` }}
                >
                  {planPill.label}
                </span>
              )}
            </div>
            {onLogout && (
              <button
                onClick={onLogout}
                className="text-xs text-rf-text-muted hover:text-rf-text-secondary transition-colors"
              >
                Logout
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

export default AppHeader;
